import { action } from "typesafe-actions";
import { IBusinessPolicyRestrutant } from "./businessPolicyRestrutant.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

export const FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_FAILED";

export const fetchBusinessPolicyRestrutantListProgress = () =>
  action(FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_PROGRESS);
export const fetchBusinessPolicyRestrutantListSuccess = (
  data: IBusinessPolicyRestrutant[]
) => action(FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_SUCCESS, { data });
export const fetchBusinessPolicyRestrutantListFailed = () =>
  action(FETCH_BUSINESS_POLICY_RESTRUTANT_LIST_FAILED);

export const fetchBusinessPolicyRestrutantListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRestrutantListProgress());

      let finalUrl = `/policy/get-policy-business-restaurant?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyRestrutant[] = res.data.data;
      dispatch(fetchBusinessPolicyRestrutantListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRestrutantListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Restaurant list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
export const fetchLeadRestaurantListAsync =
  (
    scope: ILeadScheduleScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRestrutantListProgress());
      const request = buildBinderRequest(
        "restaurant",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyRestrutant[] = res.data.data;
      dispatch(fetchBusinessPolicyRestrutantListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyRestrutantListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Restaurant binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_RESTRUTANT_PROGRESS =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_PROGRESS";
export const FETCH_BUSINESS_POLICY_RESTRUTANT_SUCCESS =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_SUCCESS";
export const FETCH_BUSINESS_POLICY_RESTRUTANT_FAILED =
  "FETCH_BUSINESS_POLICY_RESTRUTANT_FAILED";

export const fetchBusinessPolicyRestrutantProgress = () =>
  action(FETCH_BUSINESS_POLICY_RESTRUTANT_PROGRESS);
export const fetchBusinessPolicyRestrutantSuccess = (
  data: IBusinessPolicyRestrutant
) => action(FETCH_BUSINESS_POLICY_RESTRUTANT_SUCCESS, { data });
export const fetchBusinessPolicyRestrutantFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_RESTRUTANT_FAILED, { errorMessage });

export const fetchBusinessPolicyRestrutantAsync =
  (
    customerPolicyId: number,
    restrutantNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRestrutantProgress());
      const res = await api.get(
        `/policy/get-policy-business-restaurant?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${restrutantNumber}`
      );
      const data: IBusinessPolicyRestrutant[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyRestrutantSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyRestrutantFailed(
            "Oops! We couldn't find any records associated with your restrutant number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyRestrutantFailed("Something went to be wrong!")
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertBusinesPolicyRestrutantAsync =
  (
    data: IBusinessPolicyRestrutant,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post(`/policy/edit-policy-business-restaurant`, {
        ...data,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Restrutant saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped single Restaurant fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map. On failure/timeout it
// flips the record to the Failed load state so the sub-tab can surface the error +
// retry. Mirrors `fetchLeadEquipmentAsync`.
export const fetchLeadRestaurantAsync =
  (
    scope: ILeadScheduleScope,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyRestrutantProgress());
      const request = buildBinderRequest(
        "restaurant",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${incrementNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyRestrutant[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyRestrutantSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyRestrutantFailed(
            "Oops! We couldn't find any records associated with your restrutant number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyRestrutantFailed("Something went to be wrong!")
      );
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Restaurant binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight.
let leadRestaurantSaveInFlight = false;

// Feature: lead-binder-additional-tabs
// Lead-scoped Restaurant save. Persists with `lead_id` and NO `customer_policy_id`
// (the scope keys come from `buildBinderRequest`; the customer-policy identifiers are
// stripped from the record). A local in-flight guard prevents a duplicate save for the
// same record while one is already running. On success it re-fetches the list by the
// same `lead_id` (Requirements 4.2, 4.3). On failure/timeout it surfaces an error; the
// caller retains the entered input (Requirement 4.6). Mirrors
// `addLeadBusinessEquipmentAsync`.
export const addLeadBusinessRestaurantAsync =
  (
    data: IBusinessPolicyRestrutant,
    scope: ILeadScheduleScope,
    onCallback: (isSuccess: boolean) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadRestaurantSaveInFlight) {
      return;
    }
    leadRestaurantSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "restaurant",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const { customer_policy_id, ...rest } =
        data as IBusinessPolicyRestrutant & {
          customer_policy_id?: unknown;
        };
      await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Restrutant saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadRestaurantListAsync(scope, status));
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Restaurant could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadRestaurantSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_BUSINESS_POLICY_RESTRUTANT =
  "CLEAR_BUSINESS_POLICY_RESTRUTANT";
export const CLEAR_BUSINESS_POLICY_RESTRUTANT_STATE =
  "CLEAR_BUSINESS_POLICY_RESTRUTANT_STATE";

export const clearBusinessPolicyRestrutantState = () =>
  action(CLEAR_BUSINESS_POLICY_RESTRUTANT_STATE);

export const clearBusinessPolicyRestrutant = () =>
  action(CLEAR_BUSINESS_POLICY_RESTRUTANT);
